import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PLAN_NAMES } from "../components/plan/PlanUI";
import { toast } from "../hooks/use-toast";
import { Lock, Loader2, Check, Plus, X, UserRound, Crown, CreditCard, ShieldCheck } from "lucide-react";

// "Minha conta": plano atual, status do pagamento e data de renovação — tudo do backend.
function MinhaConta() {
  const { user } = useAuth();
  const [uso, setUso] = useState(null);
  const [cancelando, setCancelando] = useState(false);
  useEffect(() => {
    api.get("/me/uso").then((r) => setUso(r.data)).catch(() => {});
  }, []);

  const cancelar = async () => {
    if (!window.confirm("Cancelar sua assinatura? Você continua com o plano até a data de renovação — depois volta pro Grátis.")) return;
    setCancelando(true);
    try {
      const { data } = await api.post("/me/cancelar-assinatura");
      toast({ title: "Assinatura cancelada", description: data?.mensagem });
      const r = await api.get("/me/uso");
      setUso(r.data);
    } catch (e) {
      toast({ title: "Não deu pra cancelar", description: formatApiError(e), variant: "destructive" });
    }
    setCancelando(false);
  };

  if (!uso) return null;
  const plano = uso.plano || "gratis";
  const renova = uso.plano_expira_em ? new Date(uso.plano_expira_em).toLocaleDateString("pt-BR") : null;
  const statusMap = {
    ativo: { label: "Ativa", cls: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" },
    cancelado: { label: "Cancelada (vale até o fim do período)", cls: "text-amber-300 border-amber-500/40 bg-amber-500/10" },
    vencido: { label: "Vencida", cls: "text-red-300 border-red-500/40 bg-red-500/10" },
    nenhum: { label: "Sem assinatura", cls: "text-muted-foreground border-border bg-secondary" },
  };
  const st = statusMap[uso.pagamento_status] || statusMap.nenhum;
  return (
    <div data-testid="minha-conta" className="rounded-2xl border border-border bg-card card-depth p-5 space-y-3">
      <div className="flex items-center gap-2">
        <CreditCard className="h-4 w-4 text-violet-400" />
        <h2 className="font-bold tracking-tight text-sm">Minha conta</h2>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-violet-500/40 bg-violet-500/10 px-3 py-1 text-violet-300 font-medium">
          {plano === "pro" && <Crown className="h-3.5 w-3.5 text-amber-400" />}
          Plano {PLAN_NAMES[plano] || plano}
        </span>
        {plano !== "gratis" && (
          <span className={`rounded-full border px-3 py-1 text-xs ${st.cls}`}>{st.label}</span>
        )}
        {renova && plano !== "gratis" && (
          <span className="text-xs text-muted-foreground">
            {uso.pagamento_status === "cancelado" ? "Acaba em" : "Renova em"} <strong className="text-foreground">{renova}</strong>
          </span>
        )}
        <span className="text-xs text-muted-foreground">· {user?.email}</span>
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <Link to="/planos" className="inline-block text-xs text-violet-400 hover:text-violet-300 underline underline-offset-2">
          {plano === "gratis" ? "Ver planos e assinar" : "Ver / mudar meu plano"}
        </Link>
        {uso.pagamento_status === "ativo" && plano !== "gratis" && (
          <button
            data-testid="cancelar-assinatura"
            onClick={cancelar}
            disabled={cancelando}
            className="text-xs text-red-400/80 hover:text-red-300 underline underline-offset-2 disabled:opacity-50"
          >
            {cancelando ? "Cancelando..." : "Cancelar assinatura"}
          </button>
        )}
      </div>
      {uso.pagamento_status === "ativo" && plano !== "gratis" && (
        <p className="text-[11px] text-muted-foreground">
          Se cancelar, você continua com o plano até a data de renovação.
        </p>
      )}
    </div>
  );
}

// Card "Segurança": trocar a senha desloga os outros dispositivos (token_ver sobe no backend).
function Seguranca() {
  const [atual, setAtual] = useState("");
  const [nova, setNova] = useState("");
  const [confirmar, setConfirmar] = useState("");
  const [salvando, setSalvando] = useState(false);

  const trocar = async (e) => {
    e.preventDefault();
    if (nova !== confirmar) {
      toast({ title: "As senhas não batem", description: "A confirmação precisa ser igual à senha nova.", variant: "destructive" });
      return;
    }
    setSalvando(true);
    try {
      const { data } = await api.post("/auth/change-password", { senha_atual: atual, senha_nova: nova });
      if (data?.token) localStorage.setItem("antonio_token", data.token);
      toast({ title: "Senha trocada!", description: "Os outros dispositivos foram deslogados por segurança." });
      setAtual(""); setNova(""); setConfirmar("");
    } catch (e2) {
      toast({ title: "Não deu pra trocar a senha", description: formatApiError(e2), variant: "destructive" });
    }
    setSalvando(false);
  };

  const inputCls = "w-full rounded-lg bg-secondary px-4 py-2.5 text-base sm:text-sm outline-none focus:ring-2 focus:ring-violet-500/50 transition";
  return (
    <form data-testid="trocar-senha-form" onSubmit={trocar} className="rounded-2xl border border-border bg-card card-depth p-5 space-y-3">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-violet-400" />
        <h2 className="font-bold tracking-tight text-sm">Segurança</h2>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <input type="password" required autoComplete="current-password" placeholder="Senha atual"
          value={atual} onChange={(e) => setAtual(e.target.value)} className={inputCls} />
        <input type="password" required autoComplete="new-password" placeholder="Senha nova (mín. 8)"
          value={nova} onChange={(e) => setNova(e.target.value)} className={inputCls} />
        <input type="password" required autoComplete="new-password" placeholder="Confirmar senha nova"
          value={confirmar} onChange={(e) => setConfirmar(e.target.value)} className={inputCls} />
      </div>
      <button
        type="submit"
        disabled={salvando}
        className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2 text-sm text-foreground hover:border-violet-500/50 transition disabled:opacity-60"
      >
        {salvando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Trocar senha"}
      </button>
      <p className="text-[11px] text-muted-foreground">Ao trocar a senha, todos os outros dispositivos são deslogados.</p>
    </form>
  );
}

const MATERIAS_SUGERIDAS = [
  "Matemática", "Física", "Química", "Biologia", "História",
  "Geografia", "Português", "Literatura", "Redação", "Inglês", "Filosofia",
];

const TONS = [
  { value: "", label: "Tanto faz" },
  { value: "Direto ao ponto, sem enrolar", label: "Direto ao ponto" },
  { value: "Passo a passo, bem detalhado", label: "Passo a passo detalhado" },
  { value: "Leve e descontraído, com analogias", label: "Leve e descontraído" },
  { value: "Puxado, me desafiando com perguntas", label: "Puxado / desafiador" },
];

// Área "Meu Perfil": a ficha que alimenta a personalização do tutor.
// Dado sensível — o backend só entrega/aceita a ficha do próprio aluno logado.
export default function Perfil() {
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [novaMateria, setNovaMateria] = useState("");

  useEffect(() => {
    api.get("/profile").then((r) => setProfile(r.data)).catch((e) => setError(formatApiError(e)));
  }, []);

  const set = (campo, valor) => {
    setSaved(false);
    setProfile((p) => ({ ...p, [campo]: valor }));
  };

  const toggleMateria = (m) => {
    const atuais = profile.materias_dificeis || [];
    set("materias_dificeis", atuais.includes(m) ? atuais.filter((x) => x !== m) : [...atuais, m]);
  };

  const addMateria = () => {
    const m = novaMateria.trim();
    if (!m) return;
    if (!(profile.materias_dificeis || []).includes(m)) toggleMateria(m);
    setNovaMateria("");
  };

  const salvar = async () => {
    setSaving(true);
    setError(null);
    try {
      const { data } = await api.put("/profile", { ...profile, onboarding_done: true });
      setProfile(data);
      setSaved(true);
    } catch (e) {
      setError(formatApiError(e));
    }
    setSaving(false);
  };

  if (!profile) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-violet-400" />
      </div>
    );
  }

  return (
    <div data-testid="perfil-page" className="max-w-2xl mx-auto space-y-6">
      <div className="space-y-1">
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2.5">
          <UserRound className="h-7 w-7 text-violet-400" strokeWidth={1.75} /> Meu perfil
        </h1>
        <p className="text-sm text-muted-foreground">
          É daqui que seu tutor te conhece. Quanto mais real você for, melhor ele ensina.
        </p>
      </div>

      <MinhaConta />

      <Seguranca />

      {/* aviso de privacidade */}
      <div className="flex items-start gap-3 rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.06] px-4 py-3.5">
        <Lock className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" strokeWidth={1.75} />
        <p className="text-xs text-muted-foreground leading-relaxed">
          <strong className="text-emerald-300">Só você vê isso.</strong> Esses dados são privados, protegidos pelo seu login
          e servem pra uma coisa só: personalizar o SEU tutor. Nunca são vendidos nem compartilhados.
        </p>
      </div>

      <div className="space-y-5 rounded-2xl border border-border bg-card p-5 sm:p-6">
        <Campo
          testId="perfil-como-aprendo"
          label="Como eu aprendo melhor"
          placeholder="Ex.: com exemplos práticos antes da teoria, vendo passo a passo, me testando com questões..."
          value={profile.como_aprendo}
          onChange={(v) => set("como_aprendo", v)}
        />
        <Campo
          testId="perfil-o-que-trava"
          label="O que costuma me travar"
          placeholder="Ex.: interpretar enunciados grandes, erros bobos de conta, começar a estudar..."
          value={profile.o_que_trava}
          onChange={(v) => set("o_que_trava", v)}
        />
        <Campo
          testId="perfil-objetivos"
          label="Meus objetivos"
          placeholder="Ex.: recuperar nota em Química, passar de ano tranquilo, ENEM ano que vem..."
          value={profile.objetivos}
          onChange={(v) => set("objetivos", v)}
        />

        {/* matérias difíceis */}
        <div className="space-y-2">
          <label className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
            Matérias que tenho mais dificuldade
          </label>
          <div className="flex flex-wrap gap-2">
            {MATERIAS_SUGERIDAS.map((m) => {
              const on = (profile.materias_dificeis || []).includes(m);
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => toggleMateria(m)}
                  className={`rounded-full border px-3.5 py-1.5 text-sm transition duration-200 ${
                    on
                      ? "border-violet-500/60 bg-violet-500/15 text-violet-300"
                      : "border-border text-muted-foreground hover:border-violet-500/40 hover:text-foreground"
                  }`}
                >
                  {m}
                </button>
              );
            })}
            {(profile.materias_dificeis || [])
              .filter((m) => !MATERIAS_SUGERIDAS.includes(m))
              .map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => toggleMateria(m)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-violet-500/60 bg-violet-500/15 px-3.5 py-1.5 text-sm text-violet-300"
                >
                  {m} <X className="h-3 w-3" />
                </button>
              ))}
          </div>
          <div className="flex gap-2">
            <input
              value={novaMateria}
              onChange={(e) => setNovaMateria(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addMateria(); } }}
              placeholder="Outra matéria..."
              className="flex-1 rounded-lg bg-secondary px-4 py-2.5 text-base sm:text-sm outline-none focus:ring-2 focus:ring-violet-500/50 transition"
            />
            <button
              type="button"
              onClick={addMateria}
              className="rounded-lg border border-border px-3 text-muted-foreground hover:border-violet-500/50 hover:text-foreground transition duration-200"
              title="Adicionar matéria"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* tom */}
        <div className="space-y-2">
          <label className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
            Tom que prefiro nas explicações
          </label>
          <select
            data-testid="perfil-tom"
            value={profile.tom_preferido || ""}
            onChange={(e) => set("tom_preferido", e.target.value)}
            className="w-full rounded-lg bg-secondary px-4 py-3 text-base sm:text-sm outline-none focus:ring-2 focus:ring-violet-500/50 transition"
          >
            {TONS.map((t) => (
              <option key={t.label} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        <Campo
          testId="perfil-livre"
          label="Mais alguma coisa que seu tutor deveria saber?"
          placeholder="Campo livre — escreve do seu jeito. Ex.: tenho TDAH, estudo melhor à tarde, odeio texto gigante..."
          value={profile.livre}
          onChange={(v) => set("livre", v)}
        />

        {error && (
          <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">{error}</div>
        )}

        <button
          data-testid="perfil-salvar"
          onClick={salvar}
          disabled={saving}
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-violet-600 hover:bg-violet-500 text-white font-medium px-7 py-3 transition duration-200 disabled:opacity-60 glow-violet"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : saved ? <><Check className="h-4 w-4" /> Salvo!</> : "Salvar perfil"}
        </button>
      </div>
    </div>
  );
}

function Campo({ label, placeholder, value, onChange, testId }) {
  return (
    <div className="space-y-2">
      <label className="text-xs font-medium tracking-wide text-muted-foreground uppercase">{label}</label>
      <textarea
        data-testid={testId}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={2}
        maxLength={600}
        className="w-full rounded-lg bg-secondary px-4 py-3 text-base sm:text-sm outline-none focus:ring-2 focus:ring-violet-500/50 transition resize-y min-h-[64px]"
      />
    </div>
  );
}
